import { useRouter } from 'next/router';
import { FunnelIcon } from '../icons/funnel-icon';
import Sorting from './sorting';
import { useAIModels } from '@/data/aiModels';
import { Button, Chip, ScrollShadow, Tooltip, cn } from '@nextui-org/react';
import { useCallback, useMemo } from 'react';
import { useCategories } from '@/data/category';
import { useBreakpoint } from '@/lib/hooks/use-breakpoint';
import { useTagsQuery } from '@/data/tag';

export default function ProductFilter({
  defaultActivePath = '/',
}: {
  defaultActivePath?: string;
}) {
  const router = useRouter();
  const { categories } = useCategories();
  const { aiModels } = useAIModels();
  const { tags } = useTagsQuery({ limit: 1000 });
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'sm' || breakpoint === 'xs';


  // Generic function to get array values from a query parameter
  const getArrayValuesFromQueryParam = useCallback(
    (param: string) => {
      const result =
        router.query[param] && !Array.isArray(router.query[param])
          ? router.query[param].split(',')
          : [];
      return result;
    },
    [router.query]
  );

  // Use the generic function for aiModels and categories
  const aiModelsArrayValues = getArrayValuesFromQueryParam('aiModels');
  const categoriesArrayValues = getArrayValuesFromQueryParam('categories');
  const tagsArrayValues = getArrayValuesFromQueryParam('tags');

  const hasAnyFilter = useMemo(() => {
    return aiModelsArrayValues.length > 0 || categoriesArrayValues.length > 0 || tagsArrayValues.length > 0;
  }, [aiModelsArrayValues, categoriesArrayValues, tagsArrayValues]);

  // Generic function to update the router query
  const handleRemoveChip = useCallback(
    (param: string, valueToRemove: string) => {
      const currentValues = getArrayValuesFromQueryParam(param);

      // Filter out the value to remove
      const updatedValues = currentValues.filter(
        (value) => value !== valueToRemove
      );

      // Prepare the updated query object
      const updatedQuery = { ...router.query };

      if (updatedValues.length > 0) {
        // If there are remaining values, join them and set the query parameter
        updatedQuery[param] = updatedValues.join(',');
      } else {
        // If no values are left, delete the query parameter
        delete updatedQuery[param];
      }

      // Update the router query without reloading the page
      router.push(
        {
          pathname: router.pathname,
          query: updatedQuery,
        },
        undefined,
        { scroll: false }
      );
    },
    [router.query]
  );

  return (
    <div
      className={cn(
        'app-category-filter-bar top-16 z-20 flex min-h-[54px] w-full overflow-hidden border-b border-light-400 pb-0 md:pb-6 dark:border-dark-300 sm:top-[70px] md:min-h-[70px]',
        !hasAnyFilter && isMobile ? 'hidden' : 'flex'
      )}
    >
      <div className="-mb-4 flex w-full items-start overflow-hidden">
        <div className="pb-0 md:-mb-7 flex w-full gap-3 overflow-x-auto scroll-smooth pt-1">
          <div className="hidden self-center lg:block">
            <FunnelIcon className="h-6 w-6 self-center text-dark-500" />
          </div>
          <Sorting />
          <ScrollShadow
            className="flex min-h-[36px] w-full gap-1 py-0.5 px-2"
            style={{ alignSelf: 'end' }}
            orientation="horizontal"
            hideScrollBar={true}
          >
            {aiModels?.length > 0 &&
              aiModelsArrayValues.map((value) => {
                const name = aiModels.find(
                  (item: any) => item.slug === value
                )?.name;
                return (
                  <Tooltip key={value} content={name}>
                    <Chip
                      key={value}
                      className="mb-1 bg-brand text-white dark:bg-brand-dark"
                      onClose={() => handleRemoveChip('aiModels', value)}
                    >
                      {aiModels.find((item: any) => item.slug === value)?.name}
                    </Chip>
                  </Tooltip>
                );
              })}
            {categories?.length > 0 &&
              categoriesArrayValues.map((value) => {
                const slug = value.split('\\').pop();
                const currentCategory = categories.find(
                  (item: any) => item.slug === slug
                );
                // if (!currentCategory) return null;
                // recursively create a string of names joined by ' - ' from the category tree by category parent
                const getFullCategoryName = (category: any): string => {
                  if (category.parent) {
                    return (
                      getFullCategoryName(category.parent) +
                      ' > ' +
                      category.name
                    );
                  }
                  return category.name;
                };

                if(!currentCategory) return null;

                return (
                  <Tooltip
                    key={value}
                    content={getFullCategoryName(currentCategory)}
                    placement='top-start'
                  >
                    <Chip
                      key={value}
                      className="mb-1 bg-brand text-white dark:bg-brand-dark"
                      onClose={() => handleRemoveChip('categories', value)}
                    >
                      {currentCategory?.name}
                    </Chip>
                  </Tooltip>
                );
              })}
              
            {tags?.length > 0 
              ? tagsArrayValues.map((value) => {
                const currentTag = tags.find((item: any) => item.slug === value);
                return (
                  <Tooltip key={value} content={currentTag?.name}>
                    <Chip
                      key={value}
                      className="mb-1 bg-brand text-white dark:bg-brand-dark"
                      onClose={() => handleRemoveChip('tags', value)}
                    >
                      {currentTag?.name}
                    </Chip>
                  </Tooltip>
                );
              })
              : null
            }
          </ScrollShadow>
          <Button
            disableRipple
            variant="ghost"
            className="ml-auto hidden lg:flex"
            onClick={() => {
              // clear filters except for types and orderBy
              const query = {};
              if (router.query.types) query['types'] = router.query.types;
              if (router.query.orderBy) query['orderBy'] = router.query.orderBy;

              router.push(
                {
                  pathname: router.pathname,
                  query,
                },
                undefined,
                { shallow: true }
              );
            }}
          >
            Clear Filters
          </Button>
        </div>
      </div>
    </div>
  );
}
